import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core'
import { CommonModule } from '@angular/common'
import { Observable } from 'rxjs'

import { MessageService, ConfirmationService } from 'primeng/api'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'

import { IAction } from '@satinel-system/grid'

import { peritosGridModule } from '@peritos/grid-builder'
import {
  FormPrepareService,
  peritosFormModule,
  FormComponent
} from '@peritos/form-builder'
import {
  EntityInterfaces,
  BackendService,
  BuilderFields,
  ErrorService,
  ColumnConfig,
  FieldConfig,
  FilterParam,
  TableField
} from '@peritos/api-client'

@Component({
  selector: 'app-grid-crud',
  templateUrl: './grid-crud.component.html',
  styleUrls: ['./grid-crud.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    peritosGridModule,
    peritosFormModule
  ]
})
export class GridCrudComponent<T extends keyof EntityInterfaces>
  implements OnInit
{
  @Input() table!: T
  @Input() tableModel: BuilderFields[] = []
  @Input() fieldConfig: FieldConfig<T> = {}
  @Input() columnConfig: ColumnConfig<T> = {}
  @Input() filter: (FilterParam<T> | 'AND' | 'OR')[] = []
  @Input() title?: string
  @Input() singleTitle?: string
  @Input() icon?: string
  @Input() excludeFields: (keyof EntityInterfaces[T]['post'])[] = []
  @Input() excludeColumns: TableField<T>[] = []
  @Input() baseRecord: Partial<EntityInterfaces[T]['post']> = {}
  @Input() enableAdd = true
  @Input() enableEdit = true
  @Input() enableDelete = true
  @Input() customActions: IAction<EntityInterfaces[T]['get']>[] = []

  data: EntityInterfaces[T]['get'][] = []
  loading = false
  saving = false

  showForm = false
  editingRecord: Partial<EntityInterfaces[T]['get']> = {}
  isEdit = false

  actions: IAction<EntityInterfaces[T]['get']>[] = []

  constructor(
    private backend: BackendService,
    private prepareForm: FormPrepareService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService
  ) {}

  ngOnInit() {
    this.loadData()
    this.actions = [
      ...(this.enableEdit
        ? [
            {
              icon: 'pi pi-pencil',
              color: 'p-button-success',
              handler: (row: EntityInterfaces[T]['get']) => this.edit(row)
            }
          ]
        : []),
      ...(this.enableDelete
        ? [
            {
              color: 'p-button-danger',
              icon: 'pi pi-fw pi-trash',
              handler: (row: EntityInterfaces[T]['get']) => this.delete(row)
            }
          ]
        : []),
      ...this.customActions
    ]
  }

  loadData() {
    this.loading = true
    this.backend
      .getOrLoad(this.table, { filter: this.filter, no_limit: true })
      .subscribe({
        next: (res) => {
          this.data = res
          this.loading = false
          this.cdr.detectChanges()
        },
        error: () => {
          this.loading = false
        }
      })
  }

  add() {
    if (!this.enableAdd) return
    this.editingRecord = {}
    this.isEdit = false
    this.showForm = true
  }

  edit(row: EntityInterfaces[T]['get']) {
    if (!this.enableEdit) return
    this.editingRecord = { ...row }
    this.isEdit = true
    this.showForm = true
  }

  delete(row: EntityInterfaces[T]['get']) {
    if (!this.enableDelete) return
    this.confirmationService.confirm({
      header: 'Eliminar Registro',
      message: '¿Estás seguro de eliminar este registro?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'No, cancelar',
      acceptIcon: 'pi pi-trash mr-2',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        if (!row.id) return
        this.backend.delete(this.table, row.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminado',
              detail: 'Registro eliminado correctamente'
            })
            this.loadData()
          }
        })
      }
    })
  }

  onFormSave(form: FormComponent<T>) {
    const formGroup = form.form
    if (formGroup.invalid) {
      formGroup.markAllAsTouched()
      return
    }
    const data = {
      ...formGroup.value,
      ...this.baseRecord,
      ...(this.isEdit ? { id: this.editingRecord.id } : {})
    }
    const model = this.prepareForm.prepareData(
      this.table,
      this.tableModel,
      data
    )
    const obs = this.isEdit
      ? this.backend.put(this.table, model as EntityInterfaces[T]['put'])
      : this.backend.post(this.table, model as EntityInterfaces[T]['post'])
    this.saving = true
    ;(obs as Observable<EntityInterfaces[T]['get']>).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEdit ? 'Registro actualizado' : 'Registro creado'
        })
        this.showForm = false
        this.saving = false
        this.loadData()
      },
      error: (err) => {
        this.errorService.showErrorDialog(
          this.table,
          this.tableModel,
          err,
          this.isEdit ? 'update' : 'create'
        )
        this.saving = false
        this.cdr.detectChanges()
      }
    })
  }

  onFormCancel() {
    this.showForm = false
  }
}
